import { createFileRoute } from "@tanstack/react-router";
import React, { useState, useEffect } from "react";
import {
  BadgeCheck,
  Briefcase,
  Building2,
  CheckCircle2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Save,
  User,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { IconAction } from "@/components/shared/IconAction";
import { Field } from "@/components/forms";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { usePartnerProfile, useUpdatePartnerProfile } from "@/hooks/usePartnerData";
import { useAuthStore } from "@/store/authStore";
import AvatarUploadModal from "@/components/shared/AvatarUploadModal";
import { getFileUrl } from "@/lib/api-client";

interface PartnerProfile {
  company_name?: string;
  CompanyName?: string;
  industry?: string;
  Industry?: string;
  contact_person?: string;
  ContactPerson?: string;
  contact_email?: string;
  ContactEmail?: string;
  contact_phone?: string;
  ContactPhone?: string;
  location?: string;
  Location?: string;
  website?: string;
  Website?: string;
  description?: string;
  Description?: string;
}

export const Route = createFileRoute("/partner/profile/")({ component: PartnerProfile });

function SummaryRow({
  label,
  icon,
  value,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
}) {
  return (
    <div>
      <dt className="eyebrow mb-1">{label}</dt>
      <dd className="flex items-start gap-2 text-sm break-words text-foreground">
        <span className="mt-0.5 text-muted-foreground [&_svg]:size-4" aria-hidden>
          {icon}
        </span>
        <span className="min-w-0">{value || "—"}</span>
      </dd>
    </div>
  );
}

function PartnerProfile() {
  const { user } = useAuthStore();
  const { data: profile, isLoading } = usePartnerProfile();
  const updateProfile = useUpdatePartnerProfile();
  const [saved, setSaved] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    const p = profile as PartnerProfile | undefined;
    if (p) {
      setCompanyName(p.company_name ?? p.CompanyName ?? "");
      setIndustry(p.industry ?? p.Industry ?? "");
      setContactPerson(p.contact_person ?? p.ContactPerson ?? "");
      setContactEmail(p.contact_email ?? p.ContactEmail ?? "");
      setContactPhone(p.contact_phone ?? p.ContactPhone ?? "");
      setAddress(p.location ?? p.Location ?? "");
      setWebsite(p.website ?? p.Website ?? "");
      setDescription(p.description ?? p.Description ?? "");
    }
  }, [profile]);

  const handleSave = () => {
    updateProfile.mutate({
      company_name: companyName,
      industry: industry,
      contact_person: contactPerson,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      location: address,
      website: website,
      description: description,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  void isLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organization dossier"
        subtitle="Maintain your corporate identity, operational presence, and primary contact administrative details."
        breadcrumbs={[{ label: "Dashboard", path: "/partner/dashboard" }, { label: "Profile" }]}
      />

      {saved && (
        <Alert variant="success">
          <CheckCircle2 />
          <AlertDescription className="text-foreground">
            Corporate profile updated successfully.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <SectionCard title="Identity summary">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-4">
              <Avatar className="size-20 rounded-full border border-border">
                <AvatarImage src={getFileUrl(user?.avatar)} alt="" className="object-cover" />
                <AvatarFallback className="rounded-full bg-muted text-body">
                  <Building2 className="size-8" aria-hidden />
                </AvatarFallback>
              </Avatar>
              <span
                className="absolute -top-1.5 -left-1.5 flex size-5 items-center justify-center rounded-full border-2 border-card bg-success text-success-foreground"
                aria-label="Verified"
              >
                <BadgeCheck className="size-3" />
              </span>
              <IconAction
                label="Change logo"
                icon={<Pencil />}
                variant="outline"
                onClick={() => setAvatarModalOpen(true)}
                className="absolute -right-3 -bottom-3 size-7 bg-card"
              />
            </div>

            <h2 className="text-title text-foreground">{companyName || "—"}</h2>
            {industry && (
              <Badge variant="outline" className="mt-2">
                {industry}
              </Badge>
            )}
          </div>

          <dl className="mt-5 space-y-4 border-t border-border pt-5">
            <SummaryRow label="Administrative lead" icon={<User />} value={contactPerson} />
            <SummaryRow label="Primary contact" icon={<Mail />} value={contactEmail} />
            <SummaryRow label="Direct line" icon={<Phone />} value={contactPhone} />
            <SummaryRow label="Corporate head-office" icon={<MapPin />} value={address} />
          </dl>
        </SectionCard>

        <SectionCard
          title="Corporate intelligence"
          actions={<Briefcase className="size-4 text-muted-foreground" aria-hidden />}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Legal organization name">
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            </Field>
            <Field label="Industrial sector">
              <Input value={industry} onChange={(e) => setIndustry(e.target.value)} />
            </Field>
            <Field label="Lead representative">
              <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
            </Field>
            <Field label="Official email">
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </Field>
            <Field label="Switchboard/Direct line">
              <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </Field>
            <Field label="Corporate URL">
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} />
            </Field>
            <Field label="Physical street address" className="sm:col-span-2">
              <Textarea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
            </Field>
          </div>

          <div className="mt-6 flex justify-end border-t border-border pt-4">
            <Button onClick={handleSave} className="max-sm:w-full">
              <Save />
              Commit profile updates
            </Button>
          </div>
        </SectionCard>
      </div>
      <AvatarUploadModal open={avatarModalOpen} onClose={() => setAvatarModalOpen(false)} />
    </div>
  );
}
